import React, { createContext, useContext, useReducer, useMemo, useCallback } from 'react';

const ProspectingContext = createContext(null);
export const PROSPECTING_STORAGE_KEY = 'prospecting_state_v1';

const initialState = {
  searches: {},
  searchOrder: [],
  activeSearchId: null,
};

function normalizeSearch(rawSearch, fallbackId) {
  const id = String(rawSearch?.id || fallbackId || '').trim();
  if (!id) return null;

  const type = rawSearch?.type === 'companies' ? 'companies' : 'people';
  const mode = rawSearch?.mode === 'advanced' ? 'advanced' : 'simple';
  const createdAt = Number.isFinite(rawSearch?.createdAt) ? rawSearch.createdAt : Date.now();

  return {
    id,
    name: String(rawSearch?.name || (type === 'companies' ? 'New company search' : 'New people search')),
    type,
    mode,
    setup: rawSearch?.setup || null,
    icp: rawSearch?.icp || null,
    filters: withFilterDefaults(Array.isArray(rawSearch?.filters) ? rawSearch.filters : []),
    chatMessages: Array.isArray(rawSearch?.chatMessages) ? rawSearch.chatMessages : [],
    openTableOnStart: Boolean(rawSearch?.openTableOnStart),
    createdAt,
  };
}

export function hydrateProspectingState() {
  if (typeof window === 'undefined') return initialState;

  try {
    const raw = window.localStorage.getItem(PROSPECTING_STORAGE_KEY);
    if (!raw) return initialState;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return initialState;

    const rawSearches = parsed.searches && typeof parsed.searches === 'object'
      ? parsed.searches
      : {};
    const normalizedSearches = {};

    Object.entries(rawSearches).forEach(([id, search]) => {
      const normalized = normalizeSearch(search, id);
      if (normalized) {
        normalizedSearches[normalized.id] = normalized;
      }
    });

    const normalizedOrder = Array.isArray(parsed.searchOrder)
      ? parsed.searchOrder.filter((id) => normalizedSearches[id])
      : [];

    const missingIds = Object.keys(normalizedSearches).filter((id) => !normalizedOrder.includes(id));
    const searchOrder = [...normalizedOrder, ...missingIds];

    const activeSearchId = searchOrder.includes(parsed.activeSearchId)
      ? parsed.activeSearchId
      : (searchOrder[0] || null);

    return {
      searches: normalizedSearches,
      searchOrder,
      activeSearchId,
    };
  } catch {
    return initialState;
  }
}

function makeId(prefix) {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;
  if (cryptoApi?.randomUUID) {
    return `${prefix}_${cryptoApi.randomUUID()}`;
  }

  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

function makeFilterId() {
  return makeId('flt');
}

function makeMessageId() {
  return makeId('msg');
}

function makeSearchId() {
  return makeId('srch');
}

function withFilterDefaults(filters = []) {
  return filters.map((filter) => ({
    ...filter,
    id: filter.id || makeFilterId(),
    source: filter.source || 'ai',
  }));
}

export function prospectingReducer(state, action) {
  switch (action.type) {
    case 'CREATE_SEARCH': {
      const id = action.id || makeSearchId();
      const search = {
        id,
        name: action.name || (action.searchType === 'companies' ? 'New company search' : 'New people search'),
        type: action.searchType || 'people',
        mode: 'simple',
        filters: [],
        chatMessages: [],
        createdAt: Date.now(),
      };
      return {
        ...state,
        searches: { ...state.searches, [id]: search },
        searchOrder: [id, ...state.searchOrder],
        activeSearchId: id,
      };
    }

    case 'CREATE_SEARCH_FROM_ICP': {
      const id = action.id || makeSearchId();
      const search = {
        id,
        name: action.name || action.icp?.headline || 'New prospecting search',
        type: action.searchType || action.icp?.searchType || 'people',
        mode: 'simple',
        setup: action.setup || action.icp?.setup || null,
        icp: action.icp || null,
        filters: withFilterDefaults(action.filters || action.icp?.suggestedFilters),
        chatMessages: [],
        openTableOnStart: Boolean(action.openTableOnStart),
        createdAt: Date.now(),
      };
      return {
        ...state,
        searches: { ...state.searches, [id]: search },
        searchOrder: [id, ...state.searchOrder],
        activeSearchId: id,
      };
    }

    case 'SET_ACTIVE_SEARCH':
      return { ...state, activeSearchId: action.searchId };

    case 'RENAME_SEARCH': {
      const search = state.searches[action.searchId];
      if (!search) return state;
      return {
        ...state,
        searches: { ...state.searches, [action.searchId]: { ...search, name: action.name } },
      };
    }

    case 'SET_SEARCH_TYPE': {
      const search = state.searches[action.searchId];
      if (!search || search.type === action.searchType) return state;
      return {
        ...state,
        searches: {
          ...state.searches,
          [action.searchId]: { ...search, type: action.searchType, filters: [] },
        },
      };
    }

    case 'APPLY_FILTER': {
      const search = state.searches[action.searchId];
      if (!search) return state;
      const filter = { id: makeFilterId(), source: 'ai', ...action.filter };
      return {
        ...state,
        searches: {
          ...state.searches,
          [action.searchId]: { ...search, filters: [...search.filters, filter] },
        },
      };
    }

    case 'REMOVE_FILTER': {
      const search = state.searches[action.searchId];
      if (!search) return state;
      return {
        ...state,
        searches: {
          ...state.searches,
          [action.searchId]: {
            ...search,
            filters: search.filters.filter((f) => f.id !== action.filterId),
          },
        },
      };
    }

    case 'CLEAR_FILTERS': {
      const search = state.searches[action.searchId];
      if (!search) return state;
      return {
        ...state,
        searches: {
          ...state.searches,
          [action.searchId]: { ...search, filters: [] },
        },
      };
    }

    case 'SET_SEARCH_ICP': {
      const search = state.searches[action.searchId];
      if (!search) return state;
      return {
        ...state,
        searches: {
          ...state.searches,
          [action.searchId]: { ...search, icp: action.icp },
        },
      };
    }

    case 'UPDATE_ICP_FACET': {
      const search = state.searches[action.searchId];
      if (!search?.icp) return state;
      return {
        ...state,
        searches: {
          ...state.searches,
          [action.searchId]: {
            ...search,
            icp: {
              ...search.icp,
              facets: (search.icp.facets || []).map((facet) => (
                facet.key === action.facetKey ? { ...facet, value: action.value } : facet
              )),
            },
          },
        },
      };
    }

    case 'APPLY_ICP_FILTERS': {
      const search = state.searches[action.searchId];
      if (!search) return state;
      return {
        ...state,
        searches: {
          ...state.searches,
          [action.searchId]: {
            ...search,
            filters: withFilterDefaults(action.filters || search.icp?.suggestedFilters),
          },
        },
      };
    }

    case 'MARK_TABLE_INTENT_HANDLED': {
      const search = state.searches[action.searchId];
      if (!search || !search.openTableOnStart) return state;
      return {
        ...state,
        searches: {
          ...state.searches,
          [action.searchId]: { ...search, openTableOnStart: false },
        },
      };
    }

    case 'ADD_CHAT_MESSAGE': {
      const search = state.searches[action.searchId];
      if (!search) return state;
      const message = { id: makeMessageId(), createdAt: Date.now(), ...action.message };
      return {
        ...state,
        searches: {
          ...state.searches,
          [action.searchId]: { ...search, chatMessages: [...search.chatMessages, message] },
        },
      };
    }

    case 'DELETE_SEARCH': {
      const { [action.searchId]: _, ...rest } = state.searches;
      return {
        ...state,
        searches: rest,
        searchOrder: state.searchOrder.filter((id) => id !== action.searchId),
        activeSearchId: state.activeSearchId === action.searchId ? null : state.activeSearchId,
      };
    }

    default:
      return state;
  }
}

export function ProspectingProvider({ children }) {
  const [state, dispatch] = useReducer(prospectingReducer, initialState, hydrateProspectingState);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(PROSPECTING_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage write failures (e.g. private browsing quota limitations).
    }
  }, [state]);

  const createSearch = useCallback((searchType, name) => {
    const id = makeSearchId();
    dispatch({ type: 'CREATE_SEARCH', id, searchType, name });
    return id;
  }, []);

  const createSearchFromIcp = useCallback((payload) => {
    const id = makeSearchId();
    dispatch({ type: 'CREATE_SEARCH_FROM_ICP', id, ...payload });
    return id;
  }, []);

  const setActiveSearch = useCallback((searchId) => {
    dispatch({ type: 'SET_ACTIVE_SEARCH', searchId });
  }, []);

  const renameSearch = useCallback((searchId, name) => {
    dispatch({ type: 'RENAME_SEARCH', searchId, name });
  }, []);

  const setSearchType = useCallback((searchId, searchType) => {
    dispatch({ type: 'SET_SEARCH_TYPE', searchId, searchType });
  }, []);

  const applyFilter = useCallback((searchId, filter) => {
    dispatch({ type: 'APPLY_FILTER', searchId, filter });
  }, []);

  const removeFilter = useCallback((searchId, filterId) => {
    dispatch({ type: 'REMOVE_FILTER', searchId, filterId });
  }, []);

  const clearFilters = useCallback((searchId) => {
    dispatch({ type: 'CLEAR_FILTERS', searchId });
  }, []);

  const setSearchIcp = useCallback((searchId, icp) => {
    dispatch({ type: 'SET_SEARCH_ICP', searchId, icp });
  }, []);

  const updateIcpFacet = useCallback((searchId, facetKey, value) => {
    dispatch({ type: 'UPDATE_ICP_FACET', searchId, facetKey, value });
  }, []);

  const applyIcpFilters = useCallback((searchId, filters) => {
    dispatch({ type: 'APPLY_ICP_FILTERS', searchId, filters });
  }, []);

  const markTableIntentHandled = useCallback((searchId) => {
    dispatch({ type: 'MARK_TABLE_INTENT_HANDLED', searchId });
  }, []);

  const addChatMessage = useCallback((searchId, message) => {
    dispatch({ type: 'ADD_CHAT_MESSAGE', searchId, message });
  }, []);

  const deleteSearch = useCallback((searchId) => {
    dispatch({ type: 'DELETE_SEARCH', searchId });
  }, []);

  const value = useMemo(
    () => ({
      searches: state.searches,
      searchOrder: state.searchOrder,
      activeSearchId: state.activeSearchId,
      createSearch,
      createSearchFromIcp,
      setActiveSearch,
      renameSearch,
      setSearchType,
      applyFilter,
      removeFilter,
      clearFilters,
      setSearchIcp,
      updateIcpFacet,
      applyIcpFilters,
      markTableIntentHandled,
      addChatMessage,
      deleteSearch,
    }),
    [
      state,
      createSearch,
      createSearchFromIcp,
      setActiveSearch,
      renameSearch,
      setSearchType,
      applyFilter,
      removeFilter,
      clearFilters,
      setSearchIcp,
      updateIcpFacet,
      applyIcpFilters,
      markTableIntentHandled,
      addChatMessage,
      deleteSearch,
    ]
  );

  return <ProspectingContext.Provider value={value}>{children}</ProspectingContext.Provider>;
}

export function useProspecting() {
  const ctx = useContext(ProspectingContext);
  if (!ctx) throw new Error('useProspecting must be used within a ProspectingProvider');
  return ctx;
}

export function useProspectingSearch(searchId) {
  const ctx = useProspecting();
  return searchId ? ctx.searches[searchId] : null;
}
